import { io, Socket } from "socket.io-client";
import { Notification } from "./NotificationService";

export class NotificationSocketService {
  private socket: Socket | null = null;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;

  onNewNotification?: (notification: Notification) => void;
  onNotificationUpdated?: (data: {
    notificationId: string;
    updates: Partial<Notification>;
  }) => void;
  onNotificationDeleted?: (data: { notificationId: string }) => void;
  onNotificationsRead?: (data: { notificationIds: string[] }) => void;
  onUnreadCountChanged?: (count: number) => void;
  onReconnect?: () => void;
  onMaxReconnectAttempts?: () => void;

  connect(backendUrl: string = "http://localhost:8080"): void {
    if (this.socket?.connected) {
      console.warn("Socket already connected");
      return;
    }

    this.socket = io(backendUrl, {
      withCredentials: true,
      transports: ["websocket", "polling"],
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      reconnectionAttempts: this.maxReconnectAttempts,
    });

    this.setupEventHandlers();
  }

  private setupEventHandlers(): void {
    if (!this.socket) return;

    this.socket.on("connect", () => {
      console.log("Socket connected:", this.socket?.id);
      this.reconnectAttempts = 0;
      this.onReconnect?.();
    });

    this.socket.on("disconnect", (reason) => {
      console.log("Socket disconnected:", reason);
      if (reason === "io server disconnect") {
        this.socket?.connect();
      }
    });

    this.socket.on("connect_error", (error) => {
      console.error("Socket connection error:", error);
      this.reconnectAttempts++;
      if (this.reconnectAttempts >= this.maxReconnectAttempts) {
        console.error("Max reconnection attempts reached");
        this.onMaxReconnectAttempts?.();
      }
    });

    this.setupNotificationHandlers();
  }

  private setupNotificationHandlers(): void {
    if (!this.socket) return;

    this.socket.on("notification:new", (data: Notification) => {
      console.log("New notification:", data);
      this.onNewNotification?.(data);
    });

    this.socket.on(
      "notification:updated",
      (data: { notificationId: string; updates: Partial<Notification> }) => {
        console.log("Notification updated:", data);
        this.onNotificationUpdated?.(data);
      }
    );

    this.socket.on(
      "notification:deleted",
      (data: { notificationId: string }) => {
        console.log("Notification deleted:", data);
        this.onNotificationDeleted?.(data);
      }
    );

    this.socket.on(
      "notification:read",
      (data: { notificationIds: string[] }) => {
        console.log("Notifications read:", data);
        this.onNotificationsRead?.(data);
      }
    );

    this.socket.on("notification:count", (data: { unreadCount: number }) => {
      console.log("Unread count:", data.unreadCount);
      this.onUnreadCountChanged?.(data.unreadCount);
    });
  }

  disconnect(): void {
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
    }
  }

  isConnected(): boolean {
    return this.socket?.connected ?? false;
  }
}

export const notificationSocketService = new NotificationSocketService();

